import { cn } from "@hey404/ui";
import { Photo } from "./photo";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { BookmarkCheck, Play, Trash2 } from "lucide-react";
import { btnPrimary, btnSecondary } from "./admin-ui";
import {
  Doodle,
  PageHeader,
  PaperNote,
  Sticker,
  titleClass,
} from "./design-system";
import { apiFetch } from "./lib/auth";
import { LoadingBlock } from "./loading";

// The slice of /api/courses this page reads (same query key as the dashboard,
// so it's usually already cached).
type CatalogCourse = {
  modules: Array<{
    lessons: Array<{
      id: string;
      durationMinutes: number;
      thumbnailUrl?: string | null;
    }>;
  }>;
};

type SavedLesson = {
  lessonId: string;
  lessonTitle: string;
  moduleTitle: string;
  courseId: string;
  courseTitle: string;
  courseSlug: string;
  createdAt: string;
};

type CourseGroup = {
  courseId: string;
  courseTitle: string;
  courseSlug: string;
  lessons: SavedLesson[];
};

// Client-side navigation, mirroring main.tsx's go(): the course player reads the
// ?lesson= param on the resulting render and opens that exact saved lesson.
const go = (path: string) => {
  window.history.pushState({}, "", path);
  window.dispatchEvent(new PopStateEvent("popstate"));
};

// "Guardadas": the student's saved lessons across every course (UNB2-115),
// grouped by course, each with a link to resume it and a button to un-save.
export function SavedLessons() {
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: ["student", "bookmarks"],
    queryFn: () =>
      apiFetch<{ bookmarks: SavedLesson[] }>("/api/education/bookmarks"),
  });
  const catalog = useQuery({
    queryKey: ["student", "courses"],
    queryFn: () => apiFetch<{ courses: CatalogCourse[] }>("/api/courses"),
  });
  const bookmarks = query.data?.bookmarks ?? [];

  // Group by course, preserving the newest-first order the API returns.
  const groups: CourseGroup[] = [];
  for (const bookmark of bookmarks) {
    let group = groups.find((item) => item.courseId === bookmark.courseId);
    if (!group) {
      group = {
        courseId: bookmark.courseId,
        courseTitle: bookmark.courseTitle,
        courseSlug: bookmark.courseSlug,
        lessons: [],
      };
      groups.push(group);
    }
    group.lessons.push(bookmark);
  }

  const remove = async (lessonId: string) => {
    await apiFetch(`/api/education/lessons/${lessonId}/bookmark`, {
      method: "PUT",
      body: JSON.stringify({ bookmarked: false }),
    });
    // Keep the lesson's own toggle in sync if its detail is cached.
    void queryClient.invalidateQueries({
      queryKey: ["student", "lesson", lessonId],
    });
    void query.refetch();
  };

  // Thumbnails, durations and module numbers come from the course catalog the
  // dashboard already caches; the bookmarks endpoint only carries titles.
  const lessonInfo = new Map<
    string,
    { thumbnailUrl?: string | null; durationMinutes: number; module: string }
  >();
  for (const course of catalog.data?.courses ?? [])
    course.modules.forEach((module, index) =>
      module.lessons.forEach((lesson) =>
        lessonInfo.set(lesson.id, {
          thumbnailUrl: lesson.thumbnailUrl,
          durationMinutes: lesson.durationMinutes,
          module: String(index + 1).padStart(2, "0"),
        }),
      ),
    );
  const open = (courseSlug: string, lessonId: string) =>
    go(`/courses/${courseSlug}?lesson=${lessonId}`);

  // With nothing saved the page is just the centred note, without the header.
  const empty = !query.isLoading && !groups.length;

  return (
    <>
      {!empty && (
        <PageHeader
          kicker="Mi espacio"
          title="Guardadas"
          description="Las clases que marcaste para retomar, agrupadas por curso."
          doodle="flower-yellow"
          tone="grape"
        />
      )}
      {query.isLoading ? (
        <SavedSkeleton />
      ) : groups.length ? (
        <div className="mt-10 space-y-14">
          <Sticker tone="lemon" tilt={-1.5}>
            {bookmarks.length}{" "}
            {bookmarks.length === 1 ? "clase guardada" : "clases guardadas"}
          </Sticker>
          {groups.map((group) => (
            <section
              key={group.courseId}
              aria-labelledby={`saved-${group.courseId}`}
            >
              <p className="flex items-center gap-2.5 font-mono text-xs font-bold uppercase tracking-[0.06em]">
                <span
                  aria-hidden="true"
                  className="border-ink bg-bubble border-chip inline-block size-2.5 rotate-[-15deg]"
                />
                Curso
              </p>
              <h2
                id={`saved-${group.courseId}`}
                className={cn(titleClass.section, "mt-3")}
              >
                {group.courseTitle}
                <span aria-hidden="true" className="text-bubble ml-2">
                  ✳
                </span>
              </h2>
              <ul className="mt-7 grid gap-6 md:grid-cols-2 xl:grid-cols-3">
                {group.lessons.map((lesson, index) => {
                  const info = lessonInfo.get(lesson.lessonId);
                  return (
                    <li
                      key={lesson.lessonId}
                      className="border-ink bg-bone border-surface shadow-brutal hover:shadow-brutal-lg relative flex flex-col transition-[translate,box-shadow] duration-200 hover:-translate-y-1"
                    >
                      {index === 0 && (
                        <span
                          aria-hidden="true"
                          className="washi -top-3 left-1/2 z-20 -translate-x-1/2"
                          style={{ rotate: "-4deg" }}
                        />
                      )}
                      <button
                        type="button"
                        onClick={() => open(group.courseSlug, lesson.lessonId)}
                        aria-label={`Abrir ${lesson.lessonTitle}`}
                        className="bg-ink border-ink border-b-(length:--border-surface) group relative block aspect-video cursor-pointer overflow-hidden"
                      >
                        {info?.thumbnailUrl && (
                          <Photo
                            src={info.thumbnailUrl}
                            alt=""
                            loading="lazy"
                            className="absolute inset-0 h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                          />
                        )}
                        {/* Small and translucent so the thumbnail's title
                            card stays readable underneath; at 54% to line up
                            with the card's text rather than the frame. */}
                        <span className="border-ink bg-bubble border-chip absolute left-1/2 top-[54%] grid h-10 w-10 -translate-x-1/2 -translate-y-1/2 place-items-center rounded-full opacity-60 transition-[transform,opacity] duration-200 group-hover:scale-110 group-hover:opacity-100">
                          <Play
                            size={16}
                            fill="currentColor"
                            className="ml-0.5"
                          />
                        </span>
                        <span
                          aria-hidden="true"
                          className="border-ink bg-lemon border-chip absolute right-3 top-3 grid h-9 w-9 rotate-6 place-items-center"
                        >
                          <BookmarkCheck size={17} />
                        </span>
                      </button>
                      <div className="flex flex-1 flex-col p-5">
                        <p className="text-ink-soft truncate font-mono text-xs font-bold uppercase tracking-[0.06em]">
                          {info ? `Módulo ${info.module} · ` : ""}
                          {lesson.moduleTitle}
                        </p>
                        <h3 className={cn(titleClass.card, "mt-2")}>
                          {lesson.lessonTitle}
                        </h3>
                        <p className="text-ink-soft mt-2 font-mono text-xs">
                          {info ? `${info.durationMinutes} min · ` : ""}
                          Guardada el{" "}
                          {new Date(lesson.createdAt).toLocaleDateString("es", {
                            day: "numeric",
                            month: "short",
                          })}
                        </p>
                        <div className="mt-auto flex items-center gap-3 pt-5">
                          <button
                            type="button"
                            onClick={() =>
                              open(group.courseSlug, lesson.lessonId)
                            }
                            className={`${btnPrimary} flex-1 justify-between px-4 py-2.5 text-sm`}
                          >
                            Abrir clase <span aria-hidden="true">↗</span>
                          </button>
                          <button
                            type="button"
                            onClick={() => void remove(lesson.lessonId)}
                            aria-label={`Quitar ${lesson.lessonTitle} de guardadas`}
                            title="Quitar de guardadas"
                            className={`${btnSecondary} w-11 shrink-0 px-0`}
                          >
                            <Trash2 size={16} aria-hidden="true" />
                          </button>
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ul>
            </section>
          ))}
        </div>
      ) : (
        // Centred in the space the header would take, so the note sits in the
        // middle of the page.
        <div className="grid min-h-[calc(100dvh-12rem)] place-items-center py-10">
          <div className="relative w-full max-w-xl text-center">
            {/* Outside the note: its torn edge is a clip-path. */}
            <Doodle
              name="key-lavender"
              float
              tilt={8}
              className="absolute -right-6 -top-8 z-10 w-20"
            />
            <PaperNote tilt={-1} tape="grape">
              <h2 className={titleClass.section}>
                Todavía no guardaste clases
              </h2>
              <p className="text-ink-soft mt-3">
                Cuando estés en una clase, toca{" "}
                <strong className="text-ink">“Guardar clase”</strong> para
                tenerla aquí y retomarla cuando quieras.
              </p>
            </PaperNote>
          </div>
        </div>
      )}
    </>
  );
}

// Same shape as the saved-lesson cards, so content lands in place.
function SavedSkeleton() {
  return (
    <div role="status" aria-label="Cargando guardadas" className="mt-10">
      <LoadingBlock className="h-7 w-40" />
      <LoadingBlock className="mt-14 h-3 w-20" />
      <LoadingBlock className="mt-4 h-8 w-72 max-w-full" />
      <div className="mt-7 grid gap-6 md:grid-cols-2 xl:grid-cols-3">
        {[0, 1, 2].map((index) => (
          <div key={index} className="border-ink bg-bone border-surface">
            <LoadingBlock className="aspect-video h-auto w-full" />
            <div className="p-5">
              <LoadingBlock className="h-3 w-40" />
              <LoadingBlock className="mt-3 h-7 w-4/5" />
              <LoadingBlock className="mt-3 h-3 w-32" />
              <LoadingBlock className="rounded-control mt-6 h-11 w-full" />
            </div>
          </div>
        ))}
      </div>
      <span className="sr-only">Cargando…</span>
    </div>
  );
}
